import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Calendar from '../Calendar/Calendar';
import { CalendarProvider, useCalendar } from '../../contexts/CalendarContext';
import { websocketService } from '../../services/websocketService';
import { bookingService } from '../../api/apiService';
import "../../styles/NewCalendar.css";
import NewOrdersList from './NewOrdersList';

/**
 * NewBookingsPage component using the custom Calendar component
 * This component displays new orders in a calendar view using the API
 */
const NewBookingsPageContent = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [view, setView] = useState('list');
  const { events, setEvents, fetchBookingEvents } = useCalendar();
  const [processingOrders, setProcessingOrders] = useState(new Set());
  const [allNewOrders, setAllNewOrders] = useState([]);

  // Initial filters state with "new order" preselected
  const initialFilters = {
    bookingStatus: 'new order'
  };

  // Fetch ALL new orders regardless of date range
  const fetchAllNewOrders = async () => {
    setIsLoading(true);
    try {
      console.log('Fetching all new orders...');
      
      // Fetch all bookings with "new order" status - no date restrictions
      const response = await bookingService.getAllBookings(
        1,        // page
        10000,    // limit - increased to ensure we get all new orders
        null,     // from date - no restriction
        null,     // to date - no restriction  
        'new order', // only new orders
        'canceled,deleted,talentOnHold' // exclude these
      );

      if (response && response.data && response.data.bookings) {
        console.log(`Fetched ${response.data.bookings.length} new orders`);
        console.log('API Response metadata:', {
          total: response.data.total,
          page: response.data.page,
          limit: response.data.limit,
          totalPages: response.data.totalPages
        });
        
        // Check if we might be missing data due to pagination
        if (response.data.total && response.data.total > response.data.bookings.length) {
          console.warn(`⚠️ Potential data truncation: API reports ${response.data.total} total records but only ${response.data.bookings.length} returned`);
        }
        
        // Format the events similar to CalendarContext
        const formattedEvents = response.data.bookings.map(booking => {
          // Handle date fields
          let startDate, endDate;
          
          if (booking.start_date && typeof booking.start_date === 'object' && booking.start_date._seconds) {
            startDate = new Date(booking.start_date._seconds * 1000);
          } else if (booking.start_date) {
            startDate = new Date(booking.start_date);
          } else {
            startDate = new Date();
          }
          
          if (booking.end_date && typeof booking.end_date === 'object' && booking.end_date._seconds) {
            endDate = new Date(booking.end_date._seconds * 1000);
          } else if (booking.end_date) {
            endDate = new Date(booking.end_date);
          } else {
            endDate = startDate;
          }
          
          // Extract client and talent
          const client = typeof booking.client === 'object' ? booking.client?.label || 'Unknown' 
            : booking.client || 'Unknown';
          const talent = booking.talent_name || booking.talent || '';
          
          return {
            id: booking.id || booking.firebase_id,
            title: `${client} - ${talent}`,
            start: startDate.toISOString(),
            end: endDate.toISOString(),
            start_date: booking.start_date,
            end_date: booking.end_date,
            status: booking.status,
            client: booking.client,
            position: booking.position,
            set: booking.set,
            location: booking.location,
            location_name: booking.location_name,
            location_city: booking.location_city,
            location_short: booking.location_short,
            studio: booking.studio,
            kpi: booking.kpi,
            talent_name: talent,
            talent: talent,
            created_at: booking.created_at,
            created_by: booking.created_by,
            proposed_by: booking.proposed_by,
            processing_by: booking.processing_by,
            is_being_processed: booking.is_being_processed,
            sonstige_info: booking.sonstige_info,
            start_time: booking.start_time,
            end_time: booking.end_time,
            group: booking.group,
            group_color: booking.group_color,
            groupColor: booking.groupColor
          };
        });

        setAllNewOrders(formattedEvents);
        // Also update the calendar context events for calendar view
        setEvents(formattedEvents);
      } else {
        console.error('Invalid response structure:', response);
        setAllNewOrders([]);
      }
    } catch (error) {
      console.error('Error fetching all new orders:', error);
      setAllNewOrders([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch all new orders when component mounts
  useEffect(() => {
    fetchAllNewOrders();
  }, []); // Only run once on mount

  // Initialize WebSocket connection
  useEffect(() => {
    websocketService.connect();

    // Handle booking updates
    const handleBookingUpdate = (data) => {
      console.log('Received booking update:', data);
      
      // Update processing status
      if (data.is_being_processed !== undefined) {
        setProcessingOrders(prev => {
          const newSet = new Set(prev);
          if (data.is_being_processed) {
            newSet.add(data.id);
          } else {
            newSet.delete(data.id);
          }
          return newSet;
        });
      }

      // Update both calendar events and allNewOrders
      setEvents(prevEvents => {
        // Only handle new orders
        if (data.status !== 'new order' && !prevEvents.find(e => e.id === data.id)) {
          return prevEvents;
        }

        const eventIndex = prevEvents.findIndex(event => event.id === data.id);
        if (eventIndex !== -1) {
          // Update existing event
          const updatedEvents = [...prevEvents];
          updatedEvents[eventIndex] = { 
            ...updatedEvents[eventIndex], 
            ...data,
            disabled: data.is_being_processed // Disable if being processed
          };
          // Remove if status changed from new order
          return data.status === 'new order' ? updatedEvents : updatedEvents.filter(e => e.id !== data.id);
        }
        // Add new event if it's a new order
        return data.status === 'new order' ? [...prevEvents, { ...data, disabled: data.is_being_processed }] : prevEvents;
      });

      // Also update allNewOrders
      setAllNewOrders(prevOrders => {
        // Only handle new orders
        if (data.status !== 'new order' && !prevOrders.find(e => e.id === data.id)) {
          return prevOrders;
        }

        const orderIndex = prevOrders.findIndex(order => order.id === data.id);
        if (orderIndex !== -1) {
          // Update existing order
          const updatedOrders = [...prevOrders];
          updatedOrders[orderIndex] = { 
            ...updatedOrders[orderIndex], 
            ...data,
            disabled: data.is_being_processed // Disable if being processed
          };
          // Remove if status changed from new order
          return data.status === 'new order' ? updatedOrders : updatedOrders.filter(e => e.id !== data.id);
        }
        // Add new order if it's a new order
        return data.status === 'new order' ? [...prevOrders, { ...data, disabled: data.is_being_processed }] : prevOrders;
      });
    };

    // Subscribe to booking updates
    websocketService.subscribe('booking', handleBookingUpdate);

    // Subscribe to processing status updates
    websocketService.subscribe('booking:processing', (data) => {
      setProcessingOrders(prev => {
        const newSet = new Set(prev);
        if (data.is_being_processed) {
          newSet.add(data.id);
        } else {
          newSet.delete(data.id);
        }
        return newSet;
      });
    });

    // Cleanup on unmount
    return () => {
      websocketService.unsubscribe('booking', handleBookingUpdate);
      websocketService.unsubscribe('booking:processing');
      websocketService.disconnect();
    };
  }, [setEvents, setAllNewOrders]);

  // Inside the component
  useEffect(() => {
    // Log the original events to see their structure
    if (events && events.length > 0) {
      console.log('Original events from CalendarContext:', events[0]);
    }
  }, [events]);

  // Handle event click to navigate to the propose page
  const handleEventClick = (event) => {
    if (event && event.id && !processingOrders.has(event.id)) {
      navigate(`/admin/propose?bookingId=${event.id}`);
    } else if (processingOrders.has(event.id)) {
      // Show message that order is being processed
      alert('This order is currently being processed by another user');
    }
  };

  // Switch between calendar and list views
  const switchView = (viewType) => {
    setView(viewType);
  };

  // Helper function to determine if a booking is early or late shift
  const getShiftType = (event) => {
    const startTime = event.start_time || event.startTime || '';
    
    if (!startTime) return 'early'; // Default to early if no time specified
    
    try {
      // Parse the time string (expecting format like "09:00", "12:30", etc.)
      const timeParts = startTime.split(':');
      if (timeParts.length >= 2) {
        const hours = parseInt(timeParts[0], 10);
        const minutes = parseInt(timeParts[1], 10);
        
        // Convert to total minutes for easier comparison
        const totalMinutes = hours * 60 + minutes;
        const noonInMinutes = 12 * 60; // 12:00 = 720 minutes
        
        return totalMinutes >= noonInMinutes ? 'late' : 'early';
      }
    } catch (error) {
      console.error('Error parsing start time:', startTime, error);
    }
    
    return 'early'; // Default to early if parsing fails
  };

  // Use allNewOrders and add disabled status
  const filteredEvents = allNewOrders.map(event => {
    // Log the event to inspect its structure
    console.log("Mapping event:", event);
    
    // Handle location data which might be an object or direct fields
    let locationName = '';
    let locationCity = '';
    let locationShort = '';
    
    if (event.location && typeof event.location === 'object') {
      locationName = event.location.name || '';
      locationCity = event.location.city || '';
      locationShort = event.location.short || '';
    }
    
    // Use directly available fields if present
    locationName = event.location_name || locationName;
    locationCity = event.location_city || locationCity;
    locationShort = event.location_short || locationShort;
    
    // Determine shift type for this event
    const shiftType = getShiftType(event);
    const shiftClass = shiftType === 'late' ? 'late-shifts' : 'early-shifts';
    
    return {
      ...event,
      // Ensure all necessary fields are properly formatted
      id: event.id,
      title: event.title,
      start: event.start,
      end: event.end,
      client: event.client,
      position: event.position,
      set: event.set,
      kpi: event.kpi,
      option: event.option,
      status: event.status,
      
      // Important fields that might need special handling
      created_at: event.created_at,
      created_by: event.created_by,
      start_date: event.start_date,
      end_date: event.end_date,
      studio: event.studio,
      
      // Handle location information
      location: typeof event.location === 'string' ? event.location : (locationName || ''),
      location_name: locationName,
      location_city: locationCity,
      location_short: locationShort,
      
      // Handle talent information
      talent_name: event.talent_name || event.talent,
      talent: event.talent_name || event.talent,
      
      // Handle processing information
      proposed_by: event.proposed_by || event.proposedBy,
      processing_by: event.processing_by,
      is_being_processed: event.is_being_processed === true || event.is_being_processed === 1,
      sonstige_info: event.sonstige_info || event.SonstigeInfo,
      
      // Add disabled status for UI
      disabled: processingOrders.has(event.id),
      
      // Add shift information for styling
      shiftType: shiftType,
      className: shiftClass
    };
  }) || [];

  const getInitialWeek = () => {
    // Helper function to get current week number (ISO 8601)
    const getCurrentWeek = (date = new Date()) => {
      const targetDate = new Date(date);
      const start = new Date(targetDate.getFullYear(), 0, 1);
      const today = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());
      
      // Set both dates to Thursday of their respective weeks
      // This ensures we get the correct ISO week number
      start.setDate(start.getDate() + (4 - (start.getDay() || 7)));
      today.setDate(today.getDate() + (4 - (today.getDay() || 7)));
      
      const weekNumber = Math.ceil((((today - start) / 86400000) + 1) / 7);
      return weekNumber;
    };
    
    return getCurrentWeek();
  };

  return (
    <div className="content-wrapper page">
      <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '20px'}}>
        <h1>New Orders</h1>
        <div>
          <button className='big-button' onClick={() => navigate('/admin/allgroups')}>Groups</button>
          <button 
            className={view === 'calendar' ? 'big-button' : 'big-button-active'} 
            onClick={() => switchView('list')}>
            List View
          </button>
          <button 
            className={view === 'calendar' ? 'big-button-active' : 'big-button'} 
            onClick={() => switchView('calendar')}>
            Calendar View
          </button>
        </div>
      </div>
      
      <div style={{ height: "100%", marginTop: '20px', width: '100%'}}>
        {isLoading && (
          <div style={{textAlign: 'center', padding: '20px'}}>
            Loading calendar...
          </div>
        )}
        
        {view === 'calendar' ? (
          <Calendar 
            onEventClick={handleEventClick}
            initialFilters={initialFilters}
            disableStatusFilter={true}
            events={filteredEvents}
          />
        ) : (
          <NewOrdersList 
            events={filteredEvents}
            onEventClick={handleEventClick}
            initialWeek={getInitialWeek()}
          />
        )}
      </div>
    </div>
  );
};

// Wrap the component with CalendarProvider
const NewBookingsPage = () => (
  <CalendarProvider>
    <NewBookingsPageContent />
    <style jsx global>{`
      .custom-event.week-event.early-shifts {
        background: #d2f3ff !important;
        border: 1px solid #a8d8f0 !important;
      }
      
      .custom-event.week-event.late-shifts {
        background: #ffd9ad !important;
        border: 1px solid #e6c299 !important;
      }
      
      .rbc-event.early-shifts {
        background: #d2f3ff !important;
        border: 1px solid #a8d8f0 !important;
      }
      
      .rbc-event.late-shifts {
        background: #ffd9ad !important;
        border: 1px solid #e6c299 !important;
      }
      
      .custom-event.week-event.early-shifts,
      .rbc-event.early-shifts .rbc-event-content,
      .rbc-event.early-shifts .rbc-event-label {
        color: #2c3e50 !important;
      }
      
      .custom-event.week-event.late-shifts,
      .rbc-event.late-shifts .rbc-event-content,
      .rbc-event.late-shifts .rbc-event-label {
        color: #2c3e50 !important;
      }
    `}</style>
  </CalendarProvider>
);

export default NewBookingsPage;
